import { Component, ElementRef, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { DashboardService } from 'src/app/services/dashboard.service';
import {
    Chart,
    ChartConfiguration,
    ChartOptions,
    DoughnutController,
    ArcElement,
    Tooltip,
    Legend,
    Plugin
} from 'chart.js';

// Register Chart.js components
Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

@Component({
    selector: 'app-service-chart',
    templateUrl: './service-chart.component.html',
    styleUrls: ['./service-chart.component.css']
})
export class ServiceChartComponent implements OnInit, AfterViewInit {
    @ViewChild('pieChartCanvas', { static: false }) pieChartCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('donutChartCanvas', { static: false }) donutChartCanvas!: ElementRef<HTMLCanvasElement>;
    public displayPercentages: boolean = false;

    loading: boolean = true;
    stats: any = {};

    constructor(private spinner: NgxSpinnerService, private dashboardService: DashboardService) { }

    getFormattedValue(value: number, total: number): string {
        if (this.displayPercentages && total > 0) {
            const percentage = ((value / total) * 100);
            return percentage.toFixed(0);
        }
        return value.toString();
    }

    updateDonutChart() {
        this.displayPercentages = !this.displayPercentages;
    }

    // Pie (services)
    public pieChartOptions: ChartOptions<'doughnut'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    padding: 20,
                    usePointStyle: true
                }
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: (tooltipItem: any) => {
                        const dataset = tooltipItem.dataset;
                        const value = dataset.data[tooltipItem.dataIndex];
                        const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                        return this.displayPercentages
                            ? `${tooltipItem.label}: ${percentage}%`
                            : `${tooltipItem.label}: ${value}`;
                    }
                }
            },
        }
    };

    public textCenterPlugin1: Plugin<'doughnut'> = {
        id: 'textCenter',
        afterDraw: (chart: any) => {
            const { ctx } = chart;
            const dataset = chart.data.datasets[0];
            if (!dataset || !dataset.data.length) return;

            const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);

            // Obtener centro del primer arco
            const firstArc = chart.getDatasetMeta(0).data[0];
            const centerX = firstArc?.x ?? chart.width / 2;
            const centerY = firstArc?.y ?? chart.height / 2;

            ctx.save();
            ctx.font = 'bold 24px sans-serif';
            ctx.fillStyle = '#333';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(total.toString(), centerX, centerY); // 👈 total centrado
            ctx.restore();
        }
    };

    public pieChartData: ChartConfiguration<'doughnut'>['data'] = {
        labels: [],
        datasets: [{
            data: [],
            backgroundColor: [
                '#FF6384',
                '#36A2EB',
                '#FFCE56',
                '#4BC0C0',
                '#9966FF'
            ],
            borderColor: '#fff',
            borderWidth: 2
        }]
    };

    // Donut punctuality
    public donutChartOptions: ChartOptions<'doughnut'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'left',
                labels: {
                    padding: 20,
                    usePointStyle: true
                }
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: (tooltipItem: any) => {
                        const dataset = tooltipItem.dataset;
                        const value = dataset.data[tooltipItem.dataIndex];
                        const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                        return this.displayPercentages
                            ? `${tooltipItem.label}: ${percentage}%`
                            : `${tooltipItem.label}: ${value}`;
                    }
                }
            },
        }
    };
    public donutChartData: ChartConfiguration<'doughnut'>['data'] = {
        labels: [],
        datasets: [{
            data: [],
            backgroundColor: [
                '#FF6384',
                '#36A2EB',
                '#FFCE56',
                '#4BC0C0',
                '#9966FF'
            ],
            borderColor: '#fff',
            borderWidth: 2
        }]
    };

    private pieChart: Chart<'doughnut'> | null = null;
    private donutChart: Chart<'doughnut'> | null = null;

    ngOnInit(): void {
        this.spinner.show();
        // Initialize with some default data to test if charts render
        this.load();
    }

    ngAfterViewInit(): void {
    }

    load() {
        this.loading = true;
        this.dashboardService.statsServices().subscribe({
            next: (res: any) => {
                this.stats = res;

                // Services pie
                const sp = res.charts?.services_pie;
                if (sp) {
                    this.pieChartData.labels = sp.labels || [];
                    // total de servicios
                    this.pieChartData.datasets[0].data = sp.data || [];
                    if (this.pieChart) {
                        this.pieChart.update();
                    }
                } else {
                    // Set some default data for testing
                    this.pieChartData.labels = ['Test 1', 'Test 2'];
                    this.pieChartData.datasets[0].data = [10, 20];
                }

                // Punctuality donut
                const pd = res.charts?.punctuality_donut;
                if (pd) {
                    this.donutChartData.labels = pd.labels || [];
                    // puntualidad
                    this.donutChartData.datasets[0].data = pd.data || [];
                    if (this.donutChart) {
                        this.donutChart.update();
                    }
                } else {
                    // Set some default data for testing
                    //   this.donutChartData.labels = ['Puntual', 'Retrasado'];
                    this.donutChartData.datasets[0].data = [70, 30];
                }

                this.loading = false;
                this.spinner.hide();

                // Create charts after ensuring data is loaded
                this.createCharts();
            },
            error: (err: any) => {
                console.error('Error cargando estadísticas', err);
                this.loading = false;
                this.spinner.hide();
                // Even on error, try to create charts with default data
                this.createCharts();
            }
        });
    }

    private createCharts() {
        // Use setTimeout to ensure DOM is fully ready
        setTimeout(() => {
            this.createPieChart();
            this.createDonutChart();
        }, 100);
    }

    private createPieChart() {
        if (this.pieChart) {
            this.pieChart.destroy();
        }

        if (!this.pieChartCanvas?.nativeElement) {
            console.error('Pie chart canvas element not available');
            return;
        }

        const ctx = this.pieChartCanvas.nativeElement.getContext('2d');
        if (ctx) {
            this.pieChart = new Chart(ctx, {
                type: 'doughnut',
                data: this.pieChartData,
                options: this.pieChartOptions,
                plugins: [this.textCenterPlugin1]
            });
        } else {
            console.error('Could not get 2D context for pie chart');
        }
    }

    private createDonutChart() {
        if (this.donutChart) {
            this.donutChart.destroy();
        }

        if (!this.donutChartCanvas?.nativeElement) {
            console.error('Donut chart canvas element not available');
            return;
        }

        const ctx = this.donutChartCanvas.nativeElement.getContext('2d');
        if (ctx) {
            this.donutChart = new Chart(ctx, {
                type: 'doughnut',
                data: this.donutChartData,
                options: this.donutChartOptions,
                plugins: [this.textCenterPlugin1]
            });
        } else {
            console.error('Could not get 2D context for donut chart');
        }
    }
}
